import React, { useCallback, useEffect, useState } from 'react';
import { partnersAPI, uploadAPI } from '../../services/api';
import '../admin/AdminComponents.css';
import './PartnersManagement.css';

const defaultForm = {
  name: '',
  logoUrl: '',
  backgroundImageUrl: '',
  siteUrl: '',
  businessHotline: '',
  description: '',
  displayOrder: 0,
  featured: false,
  active: true
};

const PartnersManagement = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [formData, setFormData] = useState(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBackground, setUploadingBackground] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [pageSettings, setPageSettings] = useState({
    title: '',
    intro: '',
    heroUrl: ''
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const loadPageSettings = useCallback(async () => {
    try {
      const response = await partnersAPI.getPageSettings();
      if (response.success && response.data) {
        setPageSettings({
          title: response.data.partners_page_title || '',
          intro: response.data.partners_page_intro || '',
          heroUrl: response.data.partners_page_hero_url || ''
        });
      }
    } catch (error) {
      console.error('Error loading page settings:', error);
    } finally {
      setSettingsLoaded(true);
    }
  }, []);

  const loadPartners = useCallback(async () => {
    try {
      setLoading(true);
      const response = await partnersAPI.getAll();
      if (response.success) setPartners(response.data || []);
    } catch (error) {
      console.error('Error loading partners:', error);
      window.alert(error.message || 'Failed to load partners.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPartners();
    loadPageSettings();
  }, [loadPageSettings, loadPartners]);

  const handleSavePageSettings = async (event) => {
    event.preventDefault();
    if (uploadingHero || savingSettings) return;
    try {
      setSavingSettings(true);
      await partnersAPI.updatePageSettings(pageSettings);
    } catch (error) {
      console.error('Error saving page settings:', error);
      window.alert(error.message || 'Failed to save page settings.');
    } finally {
      setSavingSettings(false);
    }
  };

  const uploadImage = async (file, onComplete, setUploading) => {
    if (!file) return;
    try {
      setUploading(true);
      const response = await uploadAPI.uploadImage(file, 'partners');
      if (response.success && response.data?.imageUrl) {
        onComplete(response.data.imageUrl);
      }
    } catch (error) {
      console.error('Error uploading partner image:', error);
      window.alert(error.message || 'Failed to upload image.');
    } finally {
      setUploading(false);
    }
  };

  const handleHeroUpload = async (event) => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    await uploadImage(
      file,
      (imageUrl) => setPageSettings((current) => ({ ...current, heroUrl: imageUrl })),
      setUploadingHero
    );
    input.value = '';
  };

  const handleLogoUpload = async (event) => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    await uploadImage(
      file,
      (imageUrl) => setFormData((current) => ({ ...current, logoUrl: imageUrl })),
      setUploadingLogo
    );
    input.value = '';
  };

  const handleBackgroundUpload = async (event) => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    await uploadImage(
      file,
      (imageUrl) => setFormData((current) => ({ ...current, backgroundImageUrl: imageUrl })),
      setUploadingBackground
    );
    input.value = '';
  };

  const openCreateForm = () => {
    setEditingPartner(null);
    setFormData(defaultForm);
    setShowForm(true);
  };

  const openEditForm = (partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name || '',
      logoUrl: partner.logo_url || '',
      backgroundImageUrl: partner.background_image_url || '',
      siteUrl: partner.site_url || '',
      businessHotline: partner.business_hotline || '',
      description: partner.description || '',
      displayOrder: partner.display_order || 0,
      featured: Boolean(partner.featured),
      active: Boolean(partner.active)
    });
    setShowForm(true);
  };

  const closeForm = () => {
    if (uploadingLogo || uploadingBackground) return;
    setShowForm(false);
    setEditingPartner(null);
    setFormData(defaultForm);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (uploadingLogo || uploadingBackground || submitting) return;
    if (!formData.name.trim() || !formData.siteUrl.trim()) {
      window.alert('Name and Site URL are required.');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        ...formData,
        displayOrder: Number(formData.displayOrder) || 0
      };
      const response = editingPartner
        ? await partnersAPI.update(editingPartner.id, payload)
        : await partnersAPI.create(payload);

      if (response.success) {
        closeForm();
        await loadPartners();
      }
    } catch (error) {
      console.error('Error saving partner:', error);
      window.alert(error.message || 'Failed to save partner.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (partner) => {
    if (!window.confirm(`Permanently delete "${partner.name}"?`)) return;
    try {
      await partnersAPI.delete(partner.id);
      await loadPartners();
    } catch (error) {
      console.error('Error deleting partner:', error);
      window.alert(error.message || 'Failed to delete partner.');
    }
  };

  if (loading) {
    return (
      <div className="admin-section">
        <div className="loading-message">Loading partners...</div>
      </div>
    );
  }

  return (
    <div className="admin-section partners-admin">
      <div className="admin-section-header">
        <h2 className="admin-section-title">Partners Page Settings</h2>
      </div>

      {settingsLoaded && (
        <form className="partners-settings-form" onSubmit={handleSavePageSettings}>
          <div className="partners-settings-grid">
            <label className="admin-form-group">
              <span className="admin-form-label">Page Title</span>
              <input
                className="admin-form-input"
                value={pageSettings.title}
                onChange={(event) => setPageSettings((current) => ({
                  ...current,
                  title: event.target.value
                }))}
                placeholder="Our Partners"
              />
            </label>
            <label className="admin-form-group partners-settings-wide">
              <span className="admin-form-label">Page Intro Text</span>
              <textarea
                className="admin-form-textarea"
                rows={3}
                value={pageSettings.intro}
                onChange={(event) => setPageSettings((current) => ({
                  ...current,
                  intro: event.target.value
                }))}
                placeholder="Introductory paragraph shown on the partners page"
              />
            </label>
            <label className="admin-form-group partners-settings-wide">
              <span className="admin-form-label">Hero Image URL</span>
              <input
                className="admin-form-input"
                type="url"
                value={pageSettings.heroUrl}
                disabled={uploadingHero || savingSettings}
                onChange={(event) => setPageSettings((current) => ({
                  ...current,
                  heroUrl: event.target.value
                }))}
                placeholder="https://..."
              />
              <input
                className="partners-file-input"
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleHeroUpload}
                disabled={uploadingHero || savingSettings}
              />
              <small className="admin-form-hint">
                {uploadingHero ? 'Uploading hero image...' : 'Leave empty for the solid Veloura background.'}
              </small>
            </label>
          </div>
          <div className="admin-form-actions">
            <button type="submit" className="admin-button primary" disabled={savingSettings || uploadingHero}>
              {savingSettings ? 'Saving...' : 'Save Page Settings'}
            </button>
          </div>
        </form>
      )}

      <div className="admin-section-header">
        <h2 className="admin-section-title">Partners</h2>
        <div className="admin-section-actions">
          <button className="admin-button primary" type="button" onClick={openCreateForm}>
            + Add Partner
          </button>
        </div>
      </div>

      {partners.length === 0 ? (
        <div className="admin-empty-state">
          <p>No partners found. Create the first partner entry.</p>
        </div>
      ) : (
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Partner</th>
                <th>Business Hotline</th>
                <th>Featured</th>
                <th>Order</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {partners.map((partner) => (
                <tr key={partner.id}>
                  <td>
                    <div className="partners-admin-name">
                      {partner.logo_url && <img src={partner.logo_url} alt="" />}
                      <div>
                        <strong>{partner.name}</strong>
                        {partner.description && (
                          <span className="admin-description">{partner.description}</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>{partner.business_hotline || 'Not listed'}</td>
                  <td>
                    <span className={`admin-product-status-badge ${partner.featured ? 'positive' : 'neutral'}`}>
                      {partner.featured ? 'Featured' : 'Standard'}
                    </span>
                  </td>
                  <td>{partner.display_order || 0}</td>
                  <td>
                    <span className={`admin-product-status-badge ${partner.active ? 'positive' : 'neutral'}`}>
                      {partner.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="admin-table-actions">
                      <button
                        className="admin-button secondary small"
                        type="button"
                        onClick={() => openEditForm(partner)}
                      >
                        Edit
                      </button>
                      <button
                        className="admin-button danger small"
                        type="button"
                        onClick={() => handleDelete(partner)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="admin-modal-overlay" onClick={closeForm}>
          <div className="admin-modal-content" onClick={(event) => event.stopPropagation()}>
            <div className="admin-modal-header">
              <h3 className="admin-modal-title">
                {editingPartner ? 'Edit Partner' : 'Add Partner'}
              </h3>
              <button
                className="admin-modal-close"
                type="button"
                onClick={closeForm}
                aria-label="Close partner form"
              >
                X
              </button>
            </div>

            <form className="admin-modal-body" onSubmit={handleSubmit}>
              <label className="admin-form-group">
                <span className="admin-form-label">Name *</span>
                <input
                  className="admin-form-input"
                  value={formData.name}
                  onChange={(event) => setFormData((current) => ({
                    ...current,
                    name: event.target.value
                  }))}
                  required
                />
              </label>

              <label className="admin-form-group">
                <span className="admin-form-label">Site URL *</span>
                <input
                  className="admin-form-input"
                  type="url"
                  value={formData.siteUrl}
                  onChange={(event) => setFormData((current) => ({
                    ...current,
                    siteUrl: event.target.value
                  }))}
                  required
                />
              </label>

              <label className="admin-form-group">
                <span className="admin-form-label">Business Hotline</span>
                <input
                  className="admin-form-input"
                  value={formData.businessHotline}
                  onChange={(event) => setFormData((current) => ({
                    ...current,
                    businessHotline: event.target.value
                  }))}
                  placeholder="e.g. 414"
                />
              </label>

              <label className="admin-form-group">
                <span className="admin-form-label">Logo URL</span>
                <input
                  className="admin-form-input"
                  value={formData.logoUrl}
                  disabled={uploadingLogo || submitting}
                  onChange={(event) => setFormData((current) => ({
                    ...current,
                    logoUrl: event.target.value
                  }))}
                />
                <input
                  className="partners-file-input"
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleLogoUpload}
                  disabled={uploadingLogo || submitting}
                />
                {uploadingLogo && <small className="admin-form-hint">Uploading logo...</small>}
                {formData.logoUrl && (
                  <div className="admin-settings-preview">
                    <img src={formData.logoUrl} alt="Partner logo preview" />
                  </div>
                )}
              </label>

              <label className="admin-form-group">
                <span className="admin-form-label">Row Background Image URL</span>
                <input
                  className="admin-form-input"
                  type="url"
                  value={formData.backgroundImageUrl}
                  disabled={uploadingBackground || submitting}
                  onChange={(event) => setFormData((current) => ({
                    ...current,
                    backgroundImageUrl: event.target.value
                  }))}
                  placeholder="https://..."
                />
                <input
                  className="partners-file-input"
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleBackgroundUpload}
                  disabled={uploadingBackground || submitting}
                />
                {uploadingBackground && (
                  <small className="admin-form-hint">Uploading background...</small>
                )}
                {formData.backgroundImageUrl && (
                  <div className="admin-settings-preview">
                    <img src={formData.backgroundImageUrl} alt="Partner background preview" />
                  </div>
                )}
              </label>

              <label className="admin-form-group">
                <span className="admin-form-label">Description</span>
                <textarea
                  className="admin-form-textarea"
                  rows={4}
                  value={formData.description}
                  onChange={(event) => setFormData((current) => ({
                    ...current,
                    description: event.target.value
                  }))}
                />
              </label>

              <label className="admin-form-group">
                <span className="admin-form-label">Display Order</span>
                <input
                  className="admin-form-input"
                  type="number"
                  value={formData.displayOrder}
                  onChange={(event) => setFormData((current) => ({
                    ...current,
                    displayOrder: event.target.value
                  }))}
                />
              </label>

              <div className="partners-form-toggles">
                <label className="admin-checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(event) => setFormData((current) => ({
                      ...current,
                      featured: event.target.checked
                    }))}
                  />
                  Featured Partner
                </label>
                <label className="admin-checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(event) => setFormData((current) => ({
                      ...current,
                      active: event.target.checked
                    }))}
                  />
                  Active (visible on public partners page)
                </label>
              </div>

              <div className="admin-form-actions">
                <button type="button" className="admin-button secondary" onClick={closeForm}>
                  Cancel
                </button>
                <button type="submit" className="admin-button primary" disabled={submitting || uploadingLogo || uploadingBackground}>
                  {submitting ? 'Saving...' : (editingPartner ? 'Save Changes' : 'Create Partner')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnersManagement;
